<template>
  <div
    class="lr-row"
    :class="{
      'lr-row--active': isActive,
      'lr-row--dragging': isDragging,
    }"
    draggable="true"
    @dragstart.stop="$emit('drag-start', lesson.id)"
    @dragover.prevent.stop="$emit('drag-over', index)"
    @drop.stop="$emit('drop')"
    @dragend.stop="$emit('drag-end')"
    @click="handleRowClick"
  >
    <!-- Drag handle -->
    <div
      class="lr-handle"
      aria-label="Arrastrar para reordenar"
      role="button"
      tabindex="0"
      @keydown.space.prevent="() => {}"
      @click.stop
    >
      <q-icon name="drag_indicator" size="16px" />
      <q-tooltip>Arrastrar para reordenar</q-tooltip>
    </div>

    <!-- Article icon (replaces unit badge) -->
    <q-icon name="article" size="15px" class="lr-icon" />

    <!-- Title -->
    <span class="lr-title">{{ lesson.title }}</span>

    <!-- Reorder arrows (keyboard-accessible alternative) -->
    <q-btn
      flat
      dense
      round
      icon="arrow_upward"
      size="xs"
      class="lr-action lr-action--arrow"
      aria-label="Mover lección arriba"
      :disable="isFirst"
      @click.stop="$emit('move', index, index - 1)"
    >
      <q-tooltip>Mover arriba</q-tooltip>
    </q-btn>

    <q-btn
      flat
      dense
      round
      icon="arrow_downward"
      size="xs"
      class="lr-action lr-action--arrow"
      aria-label="Mover lección abajo"
      :disable="isLast"
      @click.stop="$emit('move', index, index + 1)"
    >
      <q-tooltip>Mover abajo</q-tooltip>
    </q-btn>

    <!-- Edit / Delete -->
    <q-btn
      flat
      dense
      round
      icon="edit"
      size="xs"
      class="lr-action"
      aria-label="Editar lección"
      @click.stop="$emit('edit', lesson)"
    >
      <q-tooltip>Editar lección</q-tooltip>
    </q-btn>

    <q-btn
      flat
      dense
      round
      icon="delete"
      size="xs"
      class="lr-action lr-action--danger"
      aria-label="Eliminar lección"
      @click.stop="$emit('delete', lesson)"
    >
      <q-tooltip>Eliminar lección</q-tooltip>
    </q-btn>
  </div>
</template>

<script setup lang="ts">
import { useRouter } from 'vue-router';
import type { AdminLesson } from 'stores/useAdminCoursesStore';

const props = defineProps<{
  lesson: AdminLesson;
  courseId: string;
  index: number;
  isFirst: boolean;
  isLast: boolean;
  isDragging: boolean;
  isActive: boolean;
}>();

const emit = defineEmits<{
  (e: 'edit', lesson: AdminLesson): void;
  (e: 'delete', lesson: AdminLesson): void;
  (e: 'move', from: number, to: number): void;
  (e: 'open', lesson: AdminLesson): void;
  (e: 'drag-start', id: string): void;
  (e: 'drag-over', index: number): void;
  (e: 'drop'): void;
  (e: 'drag-end'): void;
}>();

const router = useRouter();

function handleRowClick() {
  emit('open', props.lesson);
  void router.push({
    name: 'admin-lesson-editor',
    params: { id: props.courseId, lid: props.lesson.id },
  });
}
</script>

<style scoped>
.lr-row {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 8px 5px 28px; /* 28px = 8px base + 20px structural indent */
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.12s;
  min-height: 40px;
  border-left: 3px solid transparent;
}

.lr-row:hover {
  background: var(--oa-hover-bg);
}

.lr-row--active {
  border-left-color: var(--color-ocean-light, #2e7fb5);
  background: var(--oa-active-bg);
}

.lr-row--dragging {
  opacity: 0.6;
  box-shadow: 0 2px 8px rgba(13, 43, 69, 0.18);
  cursor: grabbing;
}

.lr-handle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 40px;
  flex-shrink: 0;
  color: var(--oa-text-disabled);
  cursor: grab;
  border-radius: 4px;
  transition: color 0.12s;
}

.lr-handle:hover {
  color: var(--color-ocean-light, #2e7fb5);
}

.lr-row--dragging .lr-handle {
  cursor: grabbing;
}

.lr-icon {
  color: var(--oa-text-muted);
  flex-shrink: 0;
}

.lr-title {
  flex: 1;
  font-size: 15px;
  font-weight: 400;
  color: var(--oa-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 140px;
}

.lr-action {
  opacity: 0;
  transition: opacity 0.12s;
  color: var(--oa-text-muted);
}

.lr-row:hover .lr-action,
.lr-action--arrow {
  opacity: 1;
}

.lr-action--danger:hover {
  color: var(--color-coral, #e05a3a);
}

@media (max-width: 767px) {
  .lr-action {
    opacity: 1;
  }
}
</style>
